import { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  Popconfirm,
  message,
  Image,
  Card,
  Upload,
  Input,
  Modal,
  Form,
  Select,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
  SearchOutlined,
  DollarOutlined,
  DeleteRowOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import { DownloadOutlined } from "@ant-design/icons";

const { Option } = Select;

const SellerIndex = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState({});
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedSellerId, setSelectedSellerId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get("/panel/setting/settingCategory", {
        params: {
          per_page: "all", // برای دریافت همه دسته‌بندی‌ها
        },
      });
      setCategories(response.data.data.catHeader || []);
    } catch (error) {
      message.error("خطا در دریافت لیست دسته‌بندی‌ها");
      console.error("Error fetching categories:", error);
    }
  };

  const handleExportExcel = async (sellerId) => {
    try {
      // Show loading state
      message.loading("در حال آماده سازی فایل اکسل...", 0);

      // Call the export API
      const response = await api.post(
        "/panel/excel/export/allSellerProduct",
        {
          seller_id: sellerId,
        },
        {
          responseType: "blob", // Important for file downloads
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `products_seller_${sellerId}.xlsx`);
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.destroy();
      message.success("فایل اکسل با موفقیت دانلود شد");
    } catch (error) {
      message.destroy();
      message.error("خطا در دریافت فایل اکسل");
      console.error("Export error:", error);
    }
  };

  const fetchBrands = async (page = 1, pageSize = 25, search = "") => {
    setLoading(true);
    try {
      const response = await api.get(`/panel/seller`, {
        params: {
          page,
          per_page: pageSize,
          search: search.trim(),
        },
      });
      setBrands(response.data.data);
      setPagination({
        current: response.data.meta.current_page,
        pageSize: response.data.meta.per_page,
        total: response.data.meta.total,
      });
    } catch (error) {
      message.error("خطا در دریافت اطلاعات ");
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = debounce((value) => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchBrands(1, pagination.pageSize, value);
  }, 500);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  useEffect(() => {
    fetchBrands(1, pagination.pageSize, searchText);
  }, []);

  const handleTableChange = (pagination) => {
    fetchBrands(pagination.current, pagination.pageSize, searchText);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/panel/seller/${id}`);
      message.success("تامین کننده با موفقیت حذف شد");
      fetchBrands(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error("خطا در حذف تامین کننده");
    }
  };

  const handleDeleteProduct = async (id) => {
    try {
      await api.delete(`/panel/seller/delete-all-products/${id}`);
      message.success("محصولات تامین کننده با موفقیت حذف شد");
      fetchBrands(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error("خطا در حذف محصولات تامین کننده");
    }
  };

  const handleExcelUpload = async (file, sellerId) => {
    setUploadLoading((prev) => ({ ...prev, [sellerId]: true }));
    const formData = new FormData();
    formData.append("file", file);
    formData.append("seller_id", sellerId);

    try {
      await api.post("/panel/seller/excel/product-import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      message.success("فایل اکسل با موفقیت آپلود شد");
    } catch (error) {
      message.error("خطا در آپلود فایل اکسل");
    } finally {
      setUploadLoading((prev) => ({ ...prev, [sellerId]: false }));
    }
  };

  const showModal = (sellerId) => {
    setSelectedSellerId(sellerId);
    setIsModalVisible(true);
    fetchCategories(); // دریافت لیست دسته‌بندی‌ها
    setSelectedCategories([]); // ریست کردن انتخاب‌ها
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleModalSubmit = async (values) => {
    try {
      const payload = {
        seller_id: selectedSellerId,
        product_price_change_type: values.changeType,
        product_price_change_percentage: values.percentage,
        categories: selectedCategories, // اضافه کردن آرایه دسته‌بندی‌ها
      };

      const response = await api.post(
        `/panel/seller/change-seller-post-price`,
        payload
      );

      message.success("تغییر قیمت با موفقیت اعمال شد");
      setIsModalVisible(false);
      form.resetFields();
      setSelectedCategories([]);
    } catch (error) {
      message.error("خطا در اعمال تغییر قیمت");
      console.error("Error updating prices:", error);
    }
  };

  const columns = [
    {
      title: "شناسه",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "عنوان",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "تصویر",
      dataIndex: "image",
      key: "image",
      render: (icon) => (
        <Image src={icon} alt="تصویر تامین کننده " width={100} preview={true} />
      ),
    },
    {
      title: "عملیات",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/seller/edit/${record.id}`)}
          ></Button>
          <Button
            type="primary"
            icon={<DollarOutlined />}
            onClick={() => showModal(record.id)}
            style={{ backgroundColor: "#52c41f" }}
          >
            تغییر قیمت
          </Button>

          <Upload
            showUploadList={false}
            accept=".xlsx,.xls"
            beforeUpload={(file) => {
              handleExcelUpload(file, record.id);
              return false;
            }}
          >
            <Button
              type="primary"
              icon={<UploadOutlined />}
              loading={uploadLoading[record.id]}
              style={{ backgroundColor: "#fa8c16", color: "#fff" }}
            >
              آپلود اکسل
            </Button>
          </Upload>
          <Button
            type="primary"
            onClick={() => handleExportExcel(record.id)}
            icon={<DownloadOutlined />}
            style={{ backgroundColor: "#722ed1", color: "#fff" }}
          >
            خروجی اکسل محصولات
          </Button>
          <Popconfirm
            title="آیا از حذف این تامین کننده اطمینان دارید؟"
            onConfirm={() => handleDelete(record.id)}
            okText="بله"
            cancelText="خیر"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}></Button>
          </Popconfirm>

          <Popconfirm
            title="آیا از حذف تمام محصولات این تامین کننده اطمینان دارید؟"
            onConfirm={() => handleDeleteProduct(record.id)}
            okText="بله"
            cancelText="خیر"
          >
            <Button
              type="primary"
              style={{ backgroundColor: "#cf1322", color: "#fff" }}
              danger
              icon={<DeleteRowOutlined />}
            ></Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div>
        <div className="mb-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">‌مدیریت تامین کننده‌ها</h1>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/seller/add")}
          >
            افزودن تامین کننده جدید
          </Button>
        </div>
        <div className="mb-4">
          <Input
            placeholder="جستجوی تامین کننده..."
            prefix={<SearchOutlined />}
            onChange={handleSearchChange}
            value={searchText}
            className="max-w-md"
            allowClear
          />
        </div>

        <Table
          columns={columns}
          dataSource={brands}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `مجموع: ${total} تامین کننده`,
          }}
          onChange={handleTableChange}
        />

        <Modal
          title="تغییر قیمت محصولات تامین کننده"
          visible={isModalVisible}
          onCancel={handleModalCancel}
          footer={null}
        >
          <Form form={form} onFinish={handleModalSubmit} layout="vertical">
            <Form.Item
              name="changeType"
              label="نوع تغییر قیمت محصولات"
              rules={[
                {
                  required: true,
                  message: "لطفاً نوع تغییر قیمت را انتخاب کنید",
                },
              ]}
            >
              <Select placeholder="نوع تغییر قیمت را انتخاب کنید">
                <Option value="increase">افزایش قیمت</Option>
                <Option value="decrease">کاهش قیمت</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="percentage"
              label="درصد تغییر قیمت محصولات"
              rules={[
                {
                  required: true,
                  message: "لطفاً درصد تغییر قیمت را وارد کنید",
                },
                {
                  pattern: /^[0-9]+$/,
                  message: "لطفاً یک عدد معتبر وارد کنید",
                },
              ]}
            >
              <Input placeholder="درصد تغییر قیمت" />
            </Form.Item>

            <Form.Item
              name="categories"
              label="دسته‌بندی‌ها"
              rules={[
                {
                  required: true,
                  message: "لطفاً   دسته‌بندی را انتخاب کنید",
                },
              ]}
            >
              <Select
                mode="multiple"
                placeholder="انتخاب دسته‌بندی‌ها"
                value={selectedCategories}
                onChange={setSelectedCategories}
                style={{ width: "100%" }}
              >
                {categories.map((category) => (
                  <Option key={category.id} value={category.id}>
                    {category.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit">
                اعمال تغییرات
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Card>
  );
};

export default SellerIndex;
